import { FC, useState } from 'react';
import { HStack, VStack } from '@chakra-ui/react';

import countriesList from 'common/countries/countriesList';
import Callout from 'components/_core/Callout';
import { SunshineSelect } from 'components/_core/form';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import { StepProps } from 'components/Stepper/types';
import * as Layout from 'layouts/StepperLayout';

import { CtaButton } from '../../../components/Buttons';
import { usePhysicalCardOrderFlowContext } from '../../hooks/usePhysicalCardOrderFlowContext';
import { useUpdateStreetAddition } from '../../hooks/useUpdateStreetAddition';
import locales from './locales';
import * as S from './styles';

const options = [
  {
    // @ts-expect-error countriesList.FR is possibly 'undefined'.
    flag: countriesList.FR.flag,
    label: 'France',
    value: 'fr',
  },
] as const;

const PhysicalCardOrderUpdateDeliveryInfo: FC<StepProps> = (props) => {
  const { goBack } = props;

  const {
    flowConfig: {
      coordinates: {
        personalAddress: {
          city,
          street,
          streetAddition: initialStreetAddition,
          zip,
        },
      },
    },
  } = usePhysicalCardOrderFlowContext();
  const [
    updateStreetAddition,
    { error: updateStreetAdditionError, loading: updateStreetAdditionLoading },
  ] = useUpdateStreetAddition();

  const [streetAddition, setStreetAddition] = useState(initialStreetAddition);

  const onSubmit = async () => {
    if (streetAddition !== initialStreetAddition) {
      await updateStreetAddition({ variables: { streetAddition } });
    }
    goBack();
  };

  return (
    <S.StyledForm onValidSubmit={onSubmit}>
      <Layout.Root>
        <Layout.Body>
          <Layout.HeaderButtonsContainer>
            <BackButton
              isDisabled={updateStreetAdditionLoading}
              onClick={goBack}
            />
          </Layout.HeaderButtonsContainer>

          <Layout.BodyScrollableSection>
            <VStack alignItems="stretch" spacing="space-8">
              <Heading size="lg">{locales.title}</Heading>
              <Text size="lg">{locales.subtitle}</Text>
            </VStack>

            <VStack
              alignItems="stretch"
              marginTop="space-16"
              spacing="space-24"
            >
              <Form.Field label={locales.fieldStreetLabel}>
                <Form.Input disabled value={street} />
              </Form.Field>

              <Form.Field label={locales.fieldAdditionLabel}>
                <Form.Input
                  onChange={(e) => setStreetAddition(e.target.value)}
                  placeholder={locales.fieldAdditionPlaceholder}
                  value={streetAddition}
                />
              </Form.Field>

              <HStack spacing="space-24">
                <Form.Field flex={1} label={locales.fieldZipCodeLabel}>
                  <Form.Input disabled value={zip} />
                </Form.Field>
                <Form.Field flex={1} label={locales.fieldCityLabel}>
                  <Form.Input disabled value={city} />
                </Form.Field>
              </HStack>

              <Form.Field label={locales.fieldCountryLabel}>
                <SunshineSelect
                  components={{
                    Option: SunshineSelect.components.LanguageOption,
                    SingleValue: SunshineSelect.components.LanguageSingleValue,
                  }}
                  isDisabled
                  options={options}
                  value={options[0]}
                />
              </Form.Field>
            </VStack>

            {updateStreetAdditionError ? (
              <Callout marginTop="space-40" variant="error">
                <Callout.Content>{locales.addressUpdateError}</Callout.Content>
              </Callout>
            ) : null}
          </Layout.BodyScrollableSection>

          <Layout.FooterContainer>
            <StepperProgressBar />
            <Layout.FooterButtonsContainer>
              <CtaButton isLoading={updateStreetAdditionLoading} type="submit">
                {locales.next}
              </CtaButton>
            </Layout.FooterButtonsContainer>
          </Layout.FooterContainer>
        </Layout.Body>
      </Layout.Root>
    </S.StyledForm>
  );
};

export default PhysicalCardOrderUpdateDeliveryInfo;
